import {
  CasesQuery,
  ExternalAccountsQuery,
  FacilityFloorsQuery,
  FacilityGuardsQuery,
  FacilityTypesQuery,
  ListIncidentTypesQuery,
  ManualIncidentCategoriesQuery,
  OutstandingIncidentsGuardsQuery,
  OutstandingIncidentsOperatorsQuery,
  PacsSourcesQuery,
  StandardOperatingProceduresFilterDataQuery,
  TechnicianQuery,
  TechniciansQuery,
} from '@/graphql/generated/operations'
import {
  CaseStatus,
  EmailRecipientInput,
  ExternalSystemType,
  IncidentTypeDefinition,
  SopType,
  StreamingType,
} from '@/graphql/generated/schemas'
import { incidentTypeOptions } from '@/redux/incidents/incidentsFilterOptions'

import { getUniqueValues } from '../filterDrawers'
import {
  getArchivedLabel,
  getArchivedLabelWithSN,
  getUserRoleSuffix,
} from '../formats'
import { caseUpdateStatusOptions } from './optionConsts'
import { sortOptions } from './optionHelpers'
import {
  FacilityT,
  GenericDevicesQuery,
  GenericFacilitiesQuery,
  GenericUsersQuery,
  UserT,
} from './optionTypes'

export interface OptionI<T = string> {
  label: string
  value: T
}

export interface CreatableOptionI extends OptionI {
  __isNew__?: boolean
}

export type Options<T = string> = Array<OptionI<T>>

export type FacilityMatchedFieldT = 'name' | 'shortName' | 'address'

export interface FacilityOptionWithAddressI extends OptionI {
  subtitle?: string // Display text (short name + first address line)
  shortName?: string // Facility short name, when it has one
  metadata?: string[] // Short name + full address array, for filtering
  matchedField?: FacilityMatchedFieldT // Which field satisfied searchQuery, if any
}

export interface FacilityOptionWithCueI extends FacilityOptionWithAddressI {
  caption?: string // Plain presentation text shown below the subtitle
}

/**
 * Which field of a facility satisfies a search query, checked in priority
 * order (name, then short name, then address) so a facility whose name also
 * happens to contain the query is never tagged as an address match. Shared
 * by every facility search surface — selectors and the Facilities page.
 */
export const getFacilityMatchedField = (
  facility: { name?: string; shortName?: string; address?: string[] | null },
  searchQuery: string
): FacilityMatchedFieldT | undefined => {
  if (!searchQuery) return undefined
  const lowerCaseQuery = searchQuery.toLowerCase()

  if (facility.name?.toLowerCase().includes(lowerCaseQuery)) return 'name'
  if (facility.shortName?.toLowerCase().includes(lowerCaseQuery))
    return 'shortName'
  if (facility.address?.join(' ').toLowerCase().includes(lowerCaseQuery))
    return 'address'
  return undefined
}

export const getIncidentFacilityOptions = (
  facilitiesData: FacilityTypesQuery
): Options =>
  facilitiesData?.facilities?.edges.map((f) => ({
    label: `${f?.node?.name} - (${f?.node?.shortName})`,
    value: f?.node?.id,
  })) || []

/**
 * Builds facility select options, optionally with a subtitle (short name +
 * address) for filtering and display. When `searchQuery` is passed, each
 * option is tagged with the field that satisfied it (`matchedField`) and
 * address-only matches are sorted below name/short-name matches, so a query
 * like "ca" keeps surfacing a name match ("Call Center") above facilities
 * that merely have "ca" somewhere in their address.
 */
export const getIncidentFacilityOptionsWithAddress = (
  facilitiesData: FacilityTypesQuery,
  includeAddress = false,
  searchQuery = ''
): FacilityOptionWithAddressI[] => {
  const options =
    facilitiesData?.facilities?.edges
      .map((f) => {
        const shortName = f?.node?.shortName
        const address = f?.node?.address
        // `name` is nullable in the schema — fall back to shortName so a
        // facility with no name never renders as the literal string "null".
        const name = f?.node?.name ?? shortName ?? ''

        const subtitleParts = [shortName, address?.[0]].filter(Boolean)

        return {
          label: `${name}${f?.node?.archived ? ' - ARCHIVED' : ''}`,
          value: f?.node?.id,
          matchedField: getFacilityMatchedField(f?.node, searchQuery),
          ...(includeAddress && {
            subtitle: subtitleParts.length
              ? subtitleParts.join(' · ')
              : undefined,
            metadata: [shortName, ...(address || [])].filter(Boolean),
            ...(shortName && { shortName }),
          }),
        }
      })
      .filter((option) => Boolean(option.label)) || []

  // No search yet — just the browse list. Sort alphabetically rather than
  // trusting the server's ordering, so the default list is guaranteed
  // A-Z regardless of query-level ordering behavior.
  if (!searchQuery) {
    return [...options].sort((a, b) =>
      a.label.trim().localeCompare(b.label.trim())
    )
  }

  // Stable sort: address-only matches move below name/short-name matches,
  // preserving the server's NAME-ascending order within each group.
  return [...options].sort((a, b) => {
    const aIsAddressOnly = a.matchedField === 'address' ? 1 : 0
    const bIsAddressOnly = b.matchedField === 'address' ? 1 : 0
    return aIsAddressOnly - bIsAddressOnly
  })
}

/**
 * Tags each option with a plain-text cue when it was matched by address
 * rather than name/short name — for components whose subtitle/caption props
 * (CheckboxFilterList, MultiSelectInput) are plain presentation strings
 * rather than something that can render `matchedField` directly. Callers own
 * translation, so this stays i18n-agnostic like the rest of this file.
 */
export const getFacilityOptionsWithMatchedByAddressCue = (
  options: FacilityOptionWithAddressI[],
  matchedByAddressLabel: string
): FacilityOptionWithCueI[] =>
  options.map((option) => ({
    ...option,
    caption:
      option.matchedField === 'address' ? matchedByAddressLabel : undefined,
  }))

/**
 * Custom filter function for filtering options by label or short-name/address
 * metadata. Used with FormInputSelectControl(V2)'s filterOption prop.
 */
export const createFacilityAddressFilter = (
  option: { data: FacilityOptionWithAddressI },
  input: string
): boolean => {
  const { label, metadata } = option.data
  const lowerCaseInput = input.toLowerCase()

  if (label.toLowerCase().includes(lowerCaseInput)) return true
  if (!metadata) return false

  return metadata.join(' ').toLowerCase().includes(lowerCaseInput)
}

export const getFacilityOptions = (
  facilities: GenericFacilitiesQuery
): Options =>
  facilities?.facilityNames?.edges.map((f) => getFacilityOption(f?.node)) || []

export const getIncidentTypeDefinitionOptions = (
  incidentTypesData: ListIncidentTypesQuery
): Options =>
  incidentTypesData?.incidentTypes?.items.map((f) =>
    getIncidentTypeOption(f)
  ) || []

/**
 * Options for the incident-type *filters* (incidents table drawer, outstanding
 * incidents dashboard). Includes manual types: a filter must offer every type a
 * tenant can see in Settings, or custom categories become unfilterable
 * (ENG-34085). Sorted because `incidentTypes` has no stable server-side order
 * and an edited row moves position on refetch.
 *
 * For the device-only list, use `getDeviceIncidentTypeOptions`, which sorts
 * too.
 */
export const getIncidentTypeFilterOptions = (
  incidentTypesData: ListIncidentTypesQuery
): Options =>
  sortOptions(
    incidentTypesData?.incidentTypes?.items
      ?.filter((t) => !t.archived)
      ?.map((t) => ({ label: t.displayName, value: t.id })) || []
  )

/**
 * Device-generated incident types only. The workflow wizard pairs this with a
 * separate manual-category picker, so manual types are deliberately excluded
 * here rather than merged in.
 *
 * Sorted for the same reason as `getIncidentTypeFilterOptions`: both builders
 * return ready-to-render options, so callers never sort these themselves.
 */
export const getDeviceIncidentTypeOptions = (
  incidentTypesData: ListIncidentTypesQuery
): Options =>
  sortOptions(
    incidentTypesData?.incidentTypes?.items
      ?.filter((t) => !t.manual && !t.archived)
      ?.map((t) => ({ label: t.displayName, value: t.id })) || []
  )

export const getIncidentTypeOption = (
  incidentType: Pick<IncidentTypeDefinition, 'displayName' | 'archived' | 'id'>
): OptionI => ({
  label: getArchivedLabel(incidentType.displayName, incidentType.archived),
  value: incidentType?.id,
})

export const getTechnicianFacilityOptions = (
  techData: TechnicianQuery
): Options =>
  techData?.technician?.facilities?.map((f) => getFacilityOption(f)) || []

export const getFacilityOption = (facility: FacilityT): OptionI => ({
  label: getArchivedLabelWithSN(facility),
  value: facility?.id,
})

export const getFloorOptions = (floorsData: FacilityFloorsQuery): Options =>
  floorsData?.floors?.edges.map((f) => ({
    label: `${f?.node?.name} - (${f?.node?.facility?.name})`,
    value: f?.node?.id,
  })) || []

export const getDeviceOptions = (devicesData: GenericDevicesQuery): Options =>
  devicesData?.devices?.edges.map((f) => ({
    label: `${f?.node?.name} - (${f?.node?.facility?.name})`,
    value: f?.node?.id,
  })) || []

export const getPacsOptions = (pacsData: PacsSourcesQuery): Options =>
  pacsData?.pacsSources?.edges.map((f) => ({
    label: `${f?.node?.name}`,
    value: f?.node?.id,
  })) || []

export const getUserOption = (user: UserT): OptionI => ({
  label: `${user?.firstName || ''} ${user?.lastName || ''}${
    user?.roleName ? getUserRoleSuffix(user.roleName) : ''
  }`,
  value: user.id,
})

export const getUserOptions = (usersData: GenericUsersQuery): Options =>
  usersData?.users?.edges.map((e) => e?.node).map(getUserOption) || []

export const getSOPCreatorOptions = (
  sopData: StandardOperatingProceduresFilterDataQuery
): Options => {
  const sopCreators = sopData?.standardOperatingProcedures.edges.map((s) => ({
    label: `${s?.node?.creator?.firstName} ${s?.node?.creator?.lastName}`,
    value: s?.node?.creator?.id,
  }))
  return sopCreators ? getUniqueValues(sopCreators) : []
}

export const getTechnicianOptions = (data: TechniciansQuery): Options =>
  data?.technicians?.edges.map((f) => ({
    label: `${f?.node?.firstName} ${f?.node?.lastName} (${f?.node?.email})`,
    value: f?.node?.id,
  })) || []

export const getOutstandingIncidentsGuardsOptions = (
  data: OutstandingIncidentsGuardsQuery
): Options =>
  data?.guards?.edges.map((f) => ({
    label: `${f?.node?.firstName} ${f?.node?.lastName}`,
    value: f?.node?.id,
  })) || []

export const getOutstandingIncidentsOperatorsOptions = (
  data: OutstandingIncidentsOperatorsQuery
): Options =>
  data?.operators?.edges.map((f) => ({
    label: `${f?.node?.firstName} ${f?.node?.lastName}`,
    value: f?.node?.id,
  })) || []

export const getIncidentCategoryOptions = (
  data: ManualIncidentCategoriesQuery
) =>
  data?.manualIncidentCategories?.edges?.map((e) => ({
    label: e?.node?.name,
    value: e?.node?.id,
  })) || []

export const getIncidentTypeOptions = (data: ManualIncidentCategoriesQuery) =>
  sortOptions([...getIncidentCategoryOptions(data), ...incidentTypeOptions])

export const getCaseOwnerOptions = (caseData: CasesQuery): Options => {
  const ownerOptions = caseData?.cases?.edges.map((c) => ({
    label: `${c?.node?.owner?.firstName} ${c?.node?.owner?.lastName}`,
    value: c?.node?.owner.id,
  }))
  return ownerOptions ? getUniqueValues(ownerOptions) : []
}

export const getDefaultCaseLabel = (caseStatus: CaseStatus) => {
  return caseUpdateStatusOptions.find((i) => i.value === caseStatus).label
}

export const getCaseOptions = (caseData: CasesQuery): Options =>
  caseData?.cases?.edges.map((c) => ({
    label: `${c?.node?.name}`,
    value: c?.node?.id,
  })) || []

export const getGuardOptions = (guardsData: FacilityGuardsQuery) =>
  guardsData?.checkedInUsers?.edges?.map((e) => getGuardOption(e.node)) || []

export const getGuardOption = (guard: UserT): OptionI => ({
  label: `${guard?.firstName} ${guard?.lastName}`,
  value: guard.id,
})

export const getExternalSystemTypeOptions = () => {
  return Object.keys(ExternalSystemType).map((key) => ({
    label: key,
    value: ExternalSystemType[key],
  }))
}

/**
 * The camera streaming-type dropdown options. Ordered deliberately — On Premise
 * first, then the cloud VMS types in the order they were integrated — rather
 * than alphabetically, so the list stays stable as vendors are added.
 *
 * Lives here beside `getExternalAccountOptions` because both the single-camera
 * edit form and the Devices-table bulk "Set stream account" action need it; a
 * second copy would drift the next time a `StreamingType` is added.
 */
export const getStreamingTypeOptions = (): Options<StreamingType> => [
  { value: StreamingType.OnPremise, label: 'On Premise' },
  { value: StreamingType.VerkadaCloud, label: 'Verkada Cloud' },
  { value: StreamingType.AvaCloud, label: 'Ava Cloud' },
  { value: StreamingType.AvigilonCloud, label: 'Avigilon Cloud' },
  { value: StreamingType.ExacqvisionOnprem, label: 'ExacqVision' },
  { value: StreamingType.LumanaCloud, label: 'Lumana Cloud' },
  { value: StreamingType.ArculesCloud, label: 'Arcules Cloud' },
]

/**
 * Which `ExternalSystemType` an external account must have to stream a camera of
 * the given `StreamingType`. Mirrors the backend's `StreamingType.toExternalSystemType()`
 * in `CameraNodeResolver` (backend-kotlin-dev); `updateCamera` rejects any other
 * pairing with `INCOMPATIBLE_EXTERNAL_ACCOUNT`, so the UI filters to what will save.
 * `ON_PREMISE` has no account and is deliberately absent.
 */
export const STREAMING_TYPE_TO_EXTERNAL_SYSTEM_TYPE: Partial<
  Record<StreamingType, ExternalSystemType>
> = {
  [StreamingType.VerkadaCloud]: ExternalSystemType.Verkada,
  [StreamingType.AvaCloud]: ExternalSystemType.Ava,
  [StreamingType.AvigilonCloud]: ExternalSystemType.Avigilon,
  [StreamingType.ExacqvisionOnprem]: ExternalSystemType.ExacqvisionOnprem,
  [StreamingType.LumanaCloud]: ExternalSystemType.Lumana,
  [StreamingType.ArculesCloud]: ExternalSystemType.Arcules,
}

/**
 * The streaming types that take an external account: `getStreamingTypeOptions`
 * minus `ON_PREMISE`. For forms where the account is required (the bulk "Set
 * stream account" action), offering On Premise would be a dead end: no account
 * can match it, so Submit could never enable.
 */
export const getExternalAccountStreamingTypeOptions =
  (): Options<StreamingType> =>
    getStreamingTypeOptions().filter((option) =>
      Boolean(STREAMING_TYPE_TO_EXTERNAL_SYSTEM_TYPE[option.value])
    )

type ExternalAccountEdgesT = ExternalAccountsQuery['externalAccounts']['edges']

/**
 * The external-account edges usable for `streamingType`. With no streaming type the
 * list is untouched (the Devices filter drawer lists every account); with one that
 * has no account type (`ON_PREMISE`) nothing matches.
 */
export const getExternalAccountEdgesForStreamingType = (
  externalAccountData: ExternalAccountsQuery,
  streamingType?: StreamingType
): ExternalAccountEdgesT => {
  const edges = externalAccountData?.externalAccounts?.edges ?? []
  if (!streamingType) return edges

  const externalSystemType =
    STREAMING_TYPE_TO_EXTERNAL_SYSTEM_TYPE[streamingType]
  // Bail before filtering: an edge with no `node` reads as `undefined`, which
  // would equal an unmapped type's `undefined` and surface as a blank option.
  if (!externalSystemType) return []

  return edges.filter(
    (account) => account?.node?.externalSystemType === externalSystemType
  )
}

export const getExternalAccountOptions = (
  externalAccountData: ExternalAccountsQuery,
  streamingType?: StreamingType
): Options => {
  const externalAccountOptions = getExternalAccountEdgesForStreamingType(
    externalAccountData,
    streamingType
  ).map((account) => ({
    label: account?.node?.accountName,
    value: account?.node?.id,
  }))
  return sortOptions(externalAccountOptions)
}

/**
 * The default account is per `ExternalSystemType` on the backend
 * (`ExternalAccountService.default(type)`), so the edit form passes the camera's
 * streaming type to get the default that will actually be used for it.
 */
export const getDefaultExternalAccountOption = (
  externalAccountData: ExternalAccountsQuery,
  streamingType?: StreamingType
): OptionI => {
  const defaultOption = getExternalAccountEdgesForStreamingType(
    externalAccountData,
    streamingType
  ).find((account) => account?.node?.defaultAccount)
  return {
    label: defaultOption?.node?.accountName,
    value: defaultOption?.node?.id,
  }
}

export const getQuickReportCategoryOptions = (): OptionI<boolean>[] => [
  { value: true, label: 'Enabled' },
  { value: false, label: 'Disabled' },
]

export const getSOPTypeOptions = (): OptionI<SopType>[] => [
  {
    value: SopType.Operator,
    label: 'Operator',
  },
  {
    value: SopType.Guard,
    label: 'Guard',
  },
]

/**
 * Extracts email addresses from creatable option labels.
 * Expects labels in format "email@example.com Name" where email is the first token.
 */
export const getRecipientsEmail = (
  options: CreatableOptionI[]
): EmailRecipientInput[] => {
  return options.map((option) => {
    // Trim and split on whitespace to extract email from label
    const email = option.label.trim().split(/\s+/)[0]
    return { email }
  })
}
